import Axios from "axios";
import queryString from "query-string";
import isEmpty from "lodash/isEmpty";

// Define API URL
const API_URL = "https://pwz.pediatricwellnesszone.org/api/v1";
// const API_URL = "https://pediatric.pediatricwellnesszone.store/api/v1";

// Create Axios instance
export const axios = Axios.create({
  baseURL: API_URL,
  // timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request Interceptor to add Authorization token
axios.interceptors.request.use(
  async (config) => {
    const token = sessionStorage.getItem("access-token");
    if (!isEmpty(token)) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor for handling errors (e.g., 401 Unauthorized)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("Token expired. Please login again.");
      // Add logic to redirect to login or refresh token here
    }
    return Promise.reject(error);
  }
);

// HttpClient class with methods for making requests
export class HttpClient {
  static async get<T>(url: string, params?: object): Promise<T> {
    const query = queryString.stringify(params || {}); // Add default value {}
    const urlWithQueryString = isEmpty(query) ? url : `${url}?${query}`;
    const response = await axios.get<T>(urlWithQueryString);
    return response.data;
  }

  static async post<T>(
    url: string,
    data?: object,
    options?: object
  ): Promise<T> {
    const response = await axios.post<T>(url, data, options);
    return response.data;
  }

  static async put<T>(
    url: string,
    data?: object,
    options?: object
  ): Promise<T> {
    const response = await axios.put<T>(url, data, options);
    return response.data;
  }

  static async patch<T>(url: string, data?: object): Promise<T> {
    const response = await axios.patch<T>(url, data);
    return response.data;
  }

  static async delete<T>(url: string): Promise<T> {
    const response = await axios.delete<T>(url);
    return response.data;
  }

  static formatSearchParams(params: object): string {
    return Object.entries(params)
      .filter(([, value]) => value)
      .map(([k, v]) =>
        ["type", "categories", "tags", "author", "manufacturer"].includes(k)
          ? `${k}.slug:${v}`
          : ["is_approved"].includes(k)
          ? formatBooleanSearchParam(k, v)
          : `${k}:${v}`
      )
      .join(";");
  }
}

// Helper function for formatting boolean search params
function formatBooleanSearchParam(key: string, value: boolean): string {
  return value ? `${key}:1` : `${key}:`;
}

// Helper function to get form errors
export function getFormErrors(error: any): string | null {
  if (Axios.isAxiosError(error)) {
    return error.response?.data.message || null;
  }
  return null;
}

// Helper function to get field errors
export function getFieldErrors(error: any): object | null {
  if (Axios.isAxiosError(error)) {
    return error.response?.data.errors || null;
  }
  return null;
}
